import React from 'react'

import PostIcon from '@mui/icons-material/Book'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { Admin, CustomRoutes, EditGuesser, ListGuesser, Resource, useTranslate } from 'react-admin'
import { BrowserRouter, Route } from 'react-router-dom'
import './App.scss'

import { LXLayout as Layout } from './layout/Layout'
import { Analytics } from './pages/Analytics/Analytics'
import { artworks } from './pages/Artworks'
import { beneficiaries } from './pages/Beneficiaries'
import { ImportsAmendments } from './pages/Imports/ImportsAmendments'
import { ImportsShortsAmendments } from './pages/Imports/ImportsShortsAmendments'
import { NotFound } from './pages/NotFound'
import { products } from './pages/Products'
import { statements } from './pages/Statements'
import { youtubeChannels } from './pages/YoutubeChannels'
import { youtubeRawImportRows } from './pages/YoutubeRawImportRows'
import { youtubeRawImports } from './pages/YoutubeRawImports'
import { authProvider } from './providers/authProvider'
import { dataProvider } from './providers/dataProvider'
import { i18nProvider } from './providers/i18nProvider'

import 'ag-grid-community/styles/ag-grid.css'
import 'ag-grid-community/styles/ag-theme-alpine.css'
import 'ag-grid-community/styles/ag-theme-material.css'
import { AutomatedTreatments } from './pages/AutomatedTreatments/AutomatedTreatments'
import { Compilations } from './pages/Compilations'

import { MantineProvider, createTheme } from '@mantine/core'
import { ModalsProvider } from '@mantine/modals'
import { Notifications } from '@mantine/notifications'
import { initAxiosAuthInterceptor } from './helpers/Auth.helper'
import { Login } from './pages/Login/Login'
import { ResetPassword } from './pages/Login/ResetPassword'
import { Role } from './pages/Users/Roles/Role'
import { User } from './pages/Users/User'
import { UsersRoles } from './pages/Users/UsersRoles'

import '@mantine/core/styles.css'
import '@mantine/dates/styles.css'
import '@mantine/notifications/styles.css'
import { Exports } from './pages/Exports/Exports'
import { FileCopyPage } from './pages/FileCopy'
import { FileCopyDetailPage } from './pages/FileCopy/FileCopyDetailPage'
import { Quotas } from './pages/Quotas/Quotas'
import { VideosPromotions } from './pages/VideosPromotions'
import { VideoPromotion } from './pages/VideosPromotions/VideoPromotion'

initAxiosAuthInterceptor()

const theme = createTheme({})
const queryClient = new QueryClient()

export default function App(): JSX.Element {
  const translate = useTranslate()
  return (
    <QueryClientProvider client={queryClient}>
      <MantineProvider theme={theme} defaultColorScheme="light">
        <Notifications />
        <ModalsProvider>
          <BrowserRouter>
            <Admin
              title="Logicomix Studio"
              layout={Layout}
              loginPage={Login}
              dataProvider={dataProvider}
              authProvider={authProvider}
              i18nProvider={i18nProvider}
              catchAll={NotFound}
              disableTelemetry
            >
              <CustomRoutes noLayout>
                <Route
                  caseSensitive
                  path="/reset-password"
                  key="reset-password"
                  element={<ResetPassword />}
                />
              </CustomRoutes>
              <Resource
                name="youtubeRawImports"
                options={{ label: translate('pages.youtubeRawImports._title') }}
                {...youtubeRawImports}
              />
              <Resource
                name="beneficiaries"
                options={{ label: translate('pages.beneficiaries._title') }}
                {...beneficiaries}
              />
              <Resource
                name="products"
                options={{ label: translate('pages.products._title') }}
                {...products}
              />
              <Resource
                name="artworks"
                options={{ label: translate('pages.artworks._title') }}
                {...artworks}
              />
              <Resource name="youtubeRawImportRows" {...youtubeRawImportRows} />
              <Resource
                name="statements"
                options={{ label: translate('pages.statements._title') }}
                {...statements}
              />
              <Resource
                name="youtubeChannels"
                options={{ label: translate('pages.youtubeChannels._title') }}
                {...youtubeChannels}
              />

              <Resource name="beneficiaryTypes" list={ListGuesser} edit={EditGuesser} />
              <Resource name="productTypes" icon={PostIcon} list={ListGuesser} />
              <Resource name="periodicRates" list={ListGuesser} />
              <Resource name="youtubeClaims" />
              <Resource name="youtubeAssets" />
              <Resource name="youtubeAutoEditVideos" />
              <Resource name="compilations" />
              <Resource name="statementAdjustmentRows" />
              <Resource name="datavizPresets" />
              <CustomRoutes>
                <Route caseSensitive path="/file-copy" key="file-copy" element={<FileCopyPage />} />
                <Route
                  caseSensitive
                  path="/file-copy/:operationId"
                  key="file-copy-detail"
                  element={<FileCopyDetailPage />}
                />
                <Route caseSensitive path="/analytics" key="analytics" element={<Analytics />} />
              </CustomRoutes>
              <CustomRoutes>
                <Route caseSensitive path="/users-roles" key="users" element={<UsersRoles />}>
                  <Route
                    caseSensitive
                    path=":usersRolesTab"
                    key="users-roles-tabs"
                    element={<UsersRoles />}
                  />
                </Route>
                <Route
                  caseSensitive
                  path="/users-roles/users/:userId"
                  key="user"
                  element={<User />}
                />
                <Route
                  caseSensitive
                  path="/users-roles/users/:userId/edit"
                  key="user-edit"
                  element={<User isEdit />}
                />
                <Route
                  caseSensitive
                  path="/users-roles/roles/:roleId"
                  key="role"
                  element={<Role />}
                />
                <Route
                  caseSensitive
                  path="/users-roles/roles/:roleId/edit"
                  key="role-edit"
                  element={<Role isEdit />}
                />
              </CustomRoutes>
              <CustomRoutes>
                <Route
                  caseSensitive
                  path="/automated-treatments"
                  key="automated-treatments"
                  element={<AutomatedTreatments />}
                >
                  <Route
                    caseSensitive
                    path=":automatedTreatmentTab"
                    key="automated-treatments-tabs"
                    element={<AutomatedTreatments />}
                  />
                </Route>
              </CustomRoutes>
              <CustomRoutes>
                <Route caseSensitive path="/quotas" key="quotas" element={<Quotas />}>
                  <Route caseSensitive path=":quotasTab" key="quotas-tabs" element={<Quotas />} />
                </Route>
              </CustomRoutes>
              <CustomRoutes>
                <Route caseSensitive path="/exports" key="exports" element={<Exports />}>
                  <Route
                    caseSensitive
                    path=":exportsTab"
                    key="exports-tabs"
                    element={<Exports />}
                  />
                </Route>
              </CustomRoutes>
              <CustomRoutes>
                <Route
                  caseSensitive
                  path="/compilations"
                  key="compilations"
                  element={<Compilations />}
                />
              </CustomRoutes>
              <CustomRoutes>
                <Route
                  caseSensitive
                  path="/videosPromotions"
                  key="videosPromotions"
                  element={<VideosPromotions />}
                />
                <Route
                  caseSensitive
                  path="/videosPromotions/:videoPromotionId"
                  key="videoPromotionId"
                  element={<VideoPromotion />}
                />
              </CustomRoutes>
              <CustomRoutes>
                <Route
                  caseSensitive
                  path="/imports/:importId/amend"
                  key="amend"
                  element={<ImportsAmendments />}
                />
              </CustomRoutes>
              <CustomRoutes>
                <Route
                  caseSensitive
                  path="/imports/:importId/amendShorts"
                  key="amend-shorts"
                  element={<ImportsShortsAmendments />}
                />
              </CustomRoutes>
            </Admin>
          </BrowserRouter>
        </ModalsProvider>
      </MantineProvider>
    </QueryClientProvider>
  )
}
